import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useCookies } from 'react-cookie';
import MultiLanguageContext from '.';
import useGlobalStoreContext from '../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useProjectInfoContext from '../../Contexts/ProjectContexts/ProjectInfoContext/useProjectInfoContext';
import { invalidateLanguageDependentQueries } from './languageDependentQueries';

function readProjectLanguageCookie(projectId?: string) {
  if (!projectId || typeof document === 'undefined') {
    return undefined;
  }

  const cookieName = `current-project.${projectId}.language=`;
  const matchingCookies = document.cookie
    .split('; ')
    .filter((cookie) => cookie.startsWith(cookieName));

  if (matchingCookies.length === 0) {
    return undefined;
  }

  return decodeURIComponent(matchingCookies.at(-1)?.slice(cookieName.length) ?? '');
}

export default function MultiLanguageContextProvider({ children }: { children: React.ReactNode }) {
  const { projectId: [projectId] } = useGlobalStoreContext();
  const { project } = useProjectInfoContext();
  const [cookies, setCookies, removeCookie] = useCookies();
  const queryClient = useQueryClient();
  const cookieLanguage = React.useMemo(
    () => readProjectLanguageCookie(projectId),
    [cookies, projectId],
  );
  const [pendingLanguage, setPendingLanguage] = React.useState<string | undefined>(cookieLanguage);

  const setLanguage = React.useCallback((language: string) => {
    if (projectId) {
      const cookieName = `current-project.${projectId}.language`;
      setPendingLanguage(language);
      removeCookie(cookieName, { path: '/' });
      setCookies(cookieName, language, {
        path: '/',
        sameSite: 'none',
        secure: true,
        maxAge: 365 * 3600 * 24,
      });
      invalidateLanguageDependentQueries(queryClient, projectId);
    }
  }, [projectId, queryClient, removeCookie, setCookies]);

  React.useEffect(() => {
    if (cookieLanguage === pendingLanguage) {
      setPendingLanguage(undefined);
    }
  }, [cookieLanguage, pendingLanguage]);

  React.useEffect(() => {
    setPendingLanguage(undefined);
  }, [projectId]);

  const language = pendingLanguage ?? cookieLanguage;
  const isTranslation = React.useMemo(() => {
    if (language && project?.translations?.map(((translation) => translation.language))?.includes(language)) {
      return true;
    }
    return false;
  }, [language, project]);

  const projectLanguageOptions = React.useMemo(() => [
    { original: true, language: project?.asrLanguage, id: project?.asrLanguage },
    ...(project?.translations ?? []).map((translation) => ({ original: false, id: translation.language, language: translation.language })),
  ], [project, language]);

  const value = React.useMemo(() => ({
    setLanguage,
    language: projectLanguageOptions.find((l) => l.language === language)?.language ?? project?.asrLanguage,
    projectLanguageOptions,
    isTranslation,
  }), [cookies, projectId, setLanguage, language, projectLanguageOptions, isTranslation]);

  return (
    <MultiLanguageContext.Provider value={value}>
      {children}
    </MultiLanguageContext.Provider>
  );
}
